import { supabase } from '../lib/supabase';

/**
 * Candidate Service - Handles all candidate-related database operations
 * Uses existing schema: candidates, applications, jobs, users, orgs
 */

export const candidateService = {
  /**
   * Get candidate details with their applications and job information
   */
  async getCandidateWithApplications(candidateId) {
    try {
      const { data, error } = await supabase?.from('candidates')?.select(`
          *,
          applications(
            *,
            jobs(
              *,
              orgs(name)
            )
          )
        `)?.eq('id', candidateId)?.single();

      if (error) {
        console.error('Error fetching candidate:', error);
        return { data: null, error };
      }

      return { data, error: null };
    } catch (err) {
      console.error('Candidate fetch error:', err);
      return { data: null, error: { message: 'Failed to load candidate data' } };
    }
  },

  /**
   * Get all candidates for the current organization
   */
  async getCandidates(orgId, filters = {}) {
    try {
      let query = supabase?.from('candidates')?.select(`
          *,
          applications!inner(
            *,
            jobs(title, department)
          )
        `)?.eq('org_id', orgId);

      // Apply filters if provided
      if (filters?.search) {
        query = query?.or(`full_name.ilike.%${filters?.search}%,email.ilike.%${filters?.search}%`);
      }

      if (filters?.stage) {
        query = query?.eq('applications.stage', filters?.stage);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) {
        return { data: null, error };
      }

      return { data, error: null };
    } catch (err) {
      console.error('Candidates fetch error:', err);
      return { data: null, error: { message: 'Failed to load candidates' } };
    }
  },

  /**
   * Update application stage (including "hired" status)
   */
  async updateApplicationStage(applicationId, stage) {
    try {
      const { data, error } = await supabase?.from('applications')?.update({ 
          stage,
          updated_at: new Date()?.toISOString()
        })?.eq('id', applicationId)?.select(`
          *,
          candidates(*),
          jobs(*)
        `)?.single();

      if (error) {
        return { data: null, error };
      }

      return { data, error: null };
    } catch (err) {
      console.error('Stage update error:', err);
      return { data: null, error: { message: 'Failed to update candidate stage' } };
    }
  },

  /**
   * Hire a candidate (update application stage to "hired")
   */
  async hireCandidate(applicationId) {
    return this.updateApplicationStage(applicationId, 'hired');
  },

  /**
   * Get candidate's application for a specific job
   */
  async getCandidateApplication(candidateId, jobId) {
    try {
      const { data, error } = await supabase?.from('applications')?.select(`
          *,
          jobs(*),
          candidates(*)
        `)?.eq('candidate_id', candidateId)?.eq('job_id', jobId)?.single();

      if (error) {
        return { data: null, error };
      }

      return { data, error: null };
    } catch (err) {
      console.error('Application fetch error:', err);
      return { data: null, error: { message: 'Failed to load application' } };
    }
  },

  /**
   * Update candidate information
   */
  async updateCandidate(candidateId, updates) {
    try {
      const { data, error } = await supabase?.from('candidates')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.eq('id', candidateId)?.select()?.single();

      if (error) {
        return { data: null, error };
      }

      return { data, error: null };
    } catch (err) {
      console.error('Candidate update error:', err);
      return { data: null, error: { message: 'Failed to update candidate' } };
    }
  },

  /**
   * Get hiring pipeline stages with candidate counts
   */
  async getPipelineStats(orgId) {
    try {
      const { data, error } = await supabase?.from('applications')?.select(`
          stage,
          candidates!inner(org_id)
        `)?.eq('candidates.org_id', orgId);

      if (error) {
        return { data: null, error };
      }

      // Count candidates by stage
      const stageCounts = data?.reduce((acc, app) => {
        const stage = app?.stage || 'new';
        acc[stage] = (acc?.[stage] || 0) + 1;
        return acc;
      }, {});

      return { data: stageCounts, error: null };
    } catch (err) {
      console.error('Pipeline stats error:', err);
      return { data: null, error: { message: 'Failed to load pipeline statistics' } };
    }
  }
};